import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { backupConfigAPI } from '../api/backupConfig';
import { toast } from 'react-toastify';
import './CreateBackupConfig.css';

const CreateBackupConfig = () => {
  const { uniqueId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    configName: '',
    description: '',
    sqlServerConfig: {
      host: '',
      port: 1433,
      username: '',
      password: '',
      database: '',
      instanceName: '',
    },
    sftpServers: [
      {
        name: 'Primary SFTP',
        host: '',
        port: 22,
        username: '',
        password: '',
        remotePath: '/backups',
        isActive: true,
        priority: 1,
      },
    ],
    schedules: [
      {
        name: 'Daily Backup',
        frequency: 'daily',
        time: '02:00',
        timezone: 'UTC',
        isActive: true,
      },
    ],
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSqlConfigChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      sqlServerConfig: {
        ...formData.sqlServerConfig,
        [name]: value,
      },
    });
  };

  const handleSftpChange = (index, field, value) => {
    const newSftpServers = [...formData.sftpServers];
    newSftpServers[index][field] = value;
    setFormData({ ...formData, sftpServers: newSftpServers });
  };

  const addSftpServer = () => {
    setFormData({
      ...formData,
      sftpServers: [
        ...formData.sftpServers,
        {
          name: `SFTP Server ${formData.sftpServers.length + 1}`,
          host: '',
          port: 22,
          username: '',
          password: '',
          remotePath: '/backups',
          isActive: true,
          priority: formData.sftpServers.length + 1,
        },
      ],
    });
  };

  const removeSftpServer = (index) => {
    if (formData.sftpServers.length === 1) {
      toast.warning('At least one SFTP server is required');
      return;
    }
    const newSftpServers = formData.sftpServers.filter((_, i) => i !== index);
    setFormData({ ...formData, sftpServers: newSftpServers });
  };

  const handleScheduleChange = (index, field, value) => {
    const newSchedules = [...formData.schedules];
    newSchedules[index][field] = value;
    setFormData({ ...formData, schedules: newSchedules });
  };

  const addSchedule = () => {
    setFormData({
      ...formData,
      schedules: [
        ...formData.schedules,
        {
          name: `Schedule ${formData.schedules.length + 1}`,
          frequency: 'daily',
          time: '02:00',
          timezone: 'UTC',
          isActive: true,
        },
      ],
    });
  };

  const removeSchedule = (index) => {
    if (formData.schedules.length === 1) {
      toast.warning('At least one schedule is required');
      return;
    }
    const newSchedules = formData.schedules.filter((_, i) => i !== index);
    setFormData({ ...formData, schedules: newSchedules });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await backupConfigAPI.create(uniqueId, formData);
      toast.success('Backup configuration created successfully!');
      navigate(`/installations/${uniqueId}`);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create configuration';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-backup-config-page">
      <div className="form-container">
        <h1>Create Backup Configuration</h1>

        <form onSubmit={handleSubmit} className="backup-config-form">
          {/* Basic Info */}
          <section className="form-section">
            <h2>Basic Information</h2>
            
            <div className="form-group">
              <label htmlFor="configName">Configuration Name *</label>
              <input
                type="text"
                id="configName"
                name="configName"
                value={formData.configName}
                onChange={handleChange}
                required
                placeholder="e.g., Production Database Backup"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                placeholder="Describe this backup configuration..."
              />
            </div>
          </section>

          {/* SQL Server Config */}
          <section className="form-section">
            <h2>SQL Server Configuration</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="host">Host *</label>
                <input
                  type="text"
                  id="host"
                  name="host"
                  value={formData.sqlServerConfig.host}
                  onChange={handleSqlConfigChange}
                  required
                  placeholder="localhost or IP address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="port">Port</label>
                <input
                  type="number"
                  id="port"
                  name="port"
                  value={formData.sqlServerConfig.port}
                  onChange={handleSqlConfigChange}
                  min="1"
                  max="65535"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.sqlServerConfig.username}
                  onChange={handleSqlConfigChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.sqlServerConfig.password}
                  onChange={handleSqlConfigChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="database">Database *</label>
                <input
                  type="text"
                  id="database"
                  name="database"
                  value={formData.sqlServerConfig.database}
                  onChange={handleSqlConfigChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="instanceName">Instance Name</label>
                <input
                  type="text"
                  id="instanceName"
                  name="instanceName"
                  value={formData.sqlServerConfig.instanceName}
                  onChange={handleSqlConfigChange}
                  placeholder="Optional"
                />
              </div>
            </div>
          </section>

          {/* SFTP Servers */}
          <section className="form-section">
            <div className="section-header">
              <h2>SFTP Servers</h2>
              <button type="button" onClick={addSftpServer} className="btn btn-success">
                + Add SFTP Server
              </button>
            </div>

            {formData.sftpServers.map((sftp, index) => (
              <div key={index} className="array-item">
                <div className="array-item-header">
                  <h3>SFTP Server {index + 1}</h3>
                  {formData.sftpServers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSftpServer(index)}
                      className="btn btn-danger btn-small"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={sftp.name}
                      onChange={(e) => handleSftpChange(index, 'name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Priority</label>
                    <input
                      type="number"
                      value={sftp.priority}
                      onChange={(e) => handleSftpChange(index, 'priority', parseInt(e.target.value))}
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Host *</label>
                    <input
                      type="text"
                      value={sftp.host}
                      onChange={(e) => handleSftpChange(index, 'host', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Port</label>
                    <input
                      type="number"
                      value={sftp.port}
                      onChange={(e) => handleSftpChange(index, 'port', parseInt(e.target.value))}
                      min="1"
                      max="65535"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      value={sftp.username}
                      onChange={(e) => handleSftpChange(index, 'username', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={sftp.password}
                      onChange={(e) => handleSftpChange(index, 'password', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Remote Path</label>
                  <input
                    type="text"
                    value={sftp.remotePath}
                    onChange={(e) => handleSftpChange(index, 'remotePath', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={sftp.isActive}
                      onChange={(e) => handleSftpChange(index, 'isActive', e.target.checked)}
                    />
                    {' '}Active
                  </label>
                </div>
              </div>
            ))}
          </section>

          {/* Schedules */}
          <section className="form-section">
            <div className="section-header">
              <h2>Backup Schedules</h2>
              <button type="button" onClick={addSchedule} className="btn btn-success">
                + Add Schedule
              </button>
            </div>

            {formData.schedules.map((schedule, index) => (
              <div key={index} className="array-item">
                <div className="array-item-header">
                  <h3>Schedule {index + 1}</h3>
                  {formData.schedules.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSchedule(index)}
                      className="btn btn-danger btn-small"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={schedule.name}
                      onChange={(e) => handleScheduleChange(index, 'name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Frequency *</label>
                    <select
                      value={schedule.frequency}
                      onChange={(e) => handleScheduleChange(index, 'frequency', e.target.value)}
                      required
                    >
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Time (HH:MM) *</label>
                    <input
                      type="time"
                      value={schedule.time}
                      onChange={(e) => handleScheduleChange(index, 'time', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Timezone</label>
                    <input
                      type="text"
                      value={schedule.timezone}
                      onChange={(e) => handleScheduleChange(index, 'timezone', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={schedule.isActive}
                      onChange={(e) => handleScheduleChange(index, 'isActive', e.target.checked)}
                    />
                    {' '}Active
                  </label>
                </div>
              </div>
            ))}
          </section>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(`/installations/${uniqueId}`)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBackupConfig;